// src/data/projects.js
export const projectsData = [
  {
    id: 1,
    title:
      "Research Paper: Understanding Virtual to Real Content in Extended Reality",
    description:
      "Honours thesis investigating how virtual and real environmental elements affect spatial memory performance and user experience in XR environments.",
    longDescription:
      "This Honours research project examined how different combinations of virtual and real environmental elements affect spatial memory performance and subjective user experience in extended reality (XR) environments. Using a Meta Quest 3 HMD, eight participants completed spatial selection and memory tasks across four environmental conditions in a 2×2 factorial design comparing wall type (Virtual vs. Real) and landmark presence (Present vs. Absent). The study generated 2,016 trials and revealed that landmarks may create cognitive interference rather than spatial support, with participants showing higher accuracy in landmark-absent conditions (85.7%) compared to landmark-present conditions (75.3%). Virtual and real wall conditions demonstrated equivalent performance, suggesting that virtual boundaries provide comparable spatial reference support to physical boundaries. The Real Walls with No Landmarks condition achieved optimal performance (89.3% memory accuracy) while maintaining high presence ratings and moderate cognitive load.",
    thumbnail: "/assets/images/project-xr-thesis.jpg",
    tags: [
      "Virtual Reality",
      "Unity",
      "C#",
      "Human-Computer Interaction",
      "Research",
      "Spatial Cognition",
      "Meta Quest 3",
    ],
    featured: true,
    buttons: [
      {
        text: "Read Thesis",
        color: "purple",
        link: "https://drive.google.com/file/d/1oszfhDJw5bdDRSy5_XFAuT1kl34LArov/view?usp=sharing",
      },
      {
        text: "Project Showcase",
        color: "blue",
        link: "/xr-thesis",
      },
    ],
    images: [
      "/assets/images/project-xr-thesis-1.jpg",
      "/assets/images/project-xr-thesis-2.jpg",
    ],
    challenges: [
      "Designing controlled experimental conditions that isolate effects of virtual vs. real elements",
      "Managing cognitive load across different environmental configurations",
      "Balancing presence and immersion with spatial memory performance",
      "Implementing precise spatial tracking and data collection in VR",
      "Interpreting counterintuitive landmark interference effects",
    ],
    solutions: [
      "Developed rigorous 2×2 factorial within-subjects experimental design with Latin square counterbalancing",
      "Implemented automated Unity logging system capturing millisecond-precision performance data",
      "Used validated NASA-TLX and presence questionnaires for subjective assessment",
      "Created four distinct environmental conditions with controlled wall type and landmark presence",
      "Analyzed 2,006 trials revealing novel insights about landmark cognitive interference",
      "Integrated real physical walls with virtual environments using Quest 3 passthrough capabilities",
    ],
    category: "Research",
    supervisor: "Dr. Jay Henderson",
    institution: "Memorial University of Newfoundland",
    completionDate: "August 2025",
    courseCode: "COMP 499A/B",
    paperType: "Honours Thesis",
    showcasePath: "/xr-thesis",
  },
  {
    id: 2,
    title: "Connect 2",
    description:
      "Reimagined 2D social media platform with virtual spaces that transform online interactions beyond traditional scrolling.",
    longDescription:
      "Connect 2 is a revolutionary 2D social media platform developed during the D.O. IT (Don't Overthink It) Hackathon 2024. Moving beyond conventional one-directional scrolling, Connect 2 introduces 'spaces'—virtual environments where users can freely explore, engage, and connect with others, replicating real-life social interactions. Instead of algorithm-driven feeds, users navigate through virtual rooms, choose whom to talk to, and join ongoing conversations, creating a more authentic and immersive social experience. The platform features real-time communication, personalized spaces, multiformat content sharing, and a seamless user experience designed to mimic the fluidity and richness of in-person interactions.",
    thumbnail: "/assets/images/project-connect2.jpg",
    tags: [
      "React",
      "TypeScript",
      "Vite",
      "PocketBase",
      "Figma",
      "Real-time",
      "Social Media",
    ],
    featured: true,
    buttons: [
      {
        text: "View on GitHub",
        color: "gray",
        link: "https://github.com/ARNAB814/connect2",
      },
      {
        text: "Project Showcase",
        color: "blue",
        link: "/connect2",
      },
    ],
    images: [
      "/assets/images/project-connect2-1.jpg",
      "/assets/images/project-connect2-2.jpg",
    ],
    challenges: [
      "Breaking away from traditional linear social media models",
      "Implementing real-time interactions in 2D virtual spaces",
      "Creating natural and intuitive navigation through virtual environments",
      "Managing real-time data synchronization across multiple users",
      "Designing humanlike socialization patterns in a digital environment",
    ],
    solutions: [
      "Developed innovative 'spaces' concept for free exploration and interaction",
      "Integrated PocketBase for efficient real-time data handling and user authentication",
      "Built with React and TypeScript for scalable, type-safe development",
      "Used Vite for optimized development and fast build times",
      "Designed intuitive 2D interface with Figma for seamless user experience",
    ],
    category: "Web Development",
    hackathon: "D.O. IT (Don't Overthink It) Hackathon 2024",
    teamRole: "Backend and API integration",
    teamMembers: [
      "Hailey (Design)",
      "Ethan (Frontend/Full Stack)",
      "Arunav (Backend/API)",
      "Zuhair (Backend/API)",
      "Fume (Frontend)",
      "Gavin (Deployment)",
    ],
    showcasePath: "/connect2",
  },
  {
    id: 3,
    title: "Everything Science 2025 Website",
    description:
      "Digital passport and engagement platform for Memorial University's Faculty of Science orientation event, gamifying department exploration through QR code scanning and progressive rewards.",
    longDescription:
      "Everything Science is a comprehensive digital platform developed for Memorial University's Faculty of Science orientation event, transforming the traditional department fair into an interactive, gamified experience. The platform enables 500+ new science students to explore all 9 science departments through QR code scanning, earning progressive rewards as they complete their 'science passport.' Students register with their MUN email on their first department visit, then track their progress through Bronze, Silver, Gold, and Platinum tiers. The system features real-time progress tracking, department information pages with academic programs and career paths, automated t-shirt eligibility tracking, and a prize draw entry system that encourages comprehensive faculty exploration. The frontend is deployed on Netlify with a Railway-hosted backend providing admin controls, user management, and event analytics.",
    thumbnail: "/assets/images/project-everything-science.jpg",
    tags: [
      "React",
      "Node.js",
      "QR Codes",
      "Gamification",
      "Event Management",
      "Netlify",
      "Railway",
      "Progressive Web App",
    ],
    featured: false,
    buttons: [
      {
        text: "Frontend",
        color: "green",
        link: "https://everything-science.netlify.app",
      },
      {
        text: "Project Showcase",
        color: "blue",
        link: "/everything-science",
      },
    ],
    images: [
      "/assets/images/project-everything-science-1.jpg",
      "/assets/images/project-everything-science-2.jpg",
    ],
    challenges: [
      "Creating an intuitive user experience for 500+ students with minimal onboarding",
      "Implementing reliable QR code scanning across various devices and lighting conditions",
      "Designing a gamification system that encourages complete department exploration",
      "Managing real-time progress tracking and reward eligibility across multiple simultaneous users",
      "Building an admin system for event coordination and data management",
    ],
    solutions: [
      "Developed streamlined one-time registration requiring only name and MUN email",
      "Implemented progressive reward tiers (Bronze/Silver/Gold/Platinum) with clear visual feedback",
      "Created comprehensive department pages with academic programs, career paths, and advisor contacts",
      "Built admin dashboard on Railway for real-time event monitoring and user management",
      "Integrated troubleshooting guide directly into help section for quick problem resolution",
      "Designed mobile-first responsive interface optimized for on-the-go scanning",
    ],
    category: "Web Development",
    role: "Science Orientation Coordinator & Platform Developer",
    institution: "Memorial University Faculty of Science",
    eventDate: "September 8, 2025",
    scale: "500+ students, 9 departments",
    showcasePath: "/everything-science",
  },
  {
    id: 4,
    title: "SoleMate: Smart Fall Prevention System",
    description:
      "ML-powered wearable system that predicts falls before they happen using motion sensors and real-time analysis.",
    longDescription:
      "SoleMate is a 48-hour hackathon prototype that reimagines fall prevention technology. Unlike traditional systems that only alert after a fall, SoleMate uses 6-axis motion sensors and machine learning to predict potential falls before they occur. Built for under $50 in components, the system learns individual walking patterns in just 10 seconds and provides real-time alerts when balance becomes unstable, achieving 95% accuracy in testing. The project showcases the potential of affordable, preventive healthcare technology and demonstrates how machine learning can be applied to real-world safety challenges.",
    thumbnail: "/assets/images/Shoe.jpg",
    tags: [
      "Machine Learning",
      "IoT",
      "Raspberry Pi",
      "Python",
      "Healthcare Tech",
      "Wearable",
    ],
    featured: false,
    buttons: [
      {
        text: "View on GitHub",
        color: "gray",
        link: "https://github.com/ARNAB814/SoleMate-Hackathon",
      },
      {
        text: "Project Showcase",
        color: "cyan",
        link: "/solemate",
      },
    ],
    images: ["/assets/images/Shoe.jpg", "/assets/images/solemate_group.jpg"],
    challenges: [
      "Building a functional prototype in just 48 hours",
      "Achieving reliable fall prediction with consumer-grade sensors",
      "Minimizing false positives while maintaining high sensitivity",
      "Integrating hardware and software within tight budget constraints",
      "Creating an ML model that adapts to individual walking patterns",
    ],
    solutions: [
      "Implemented 10-second baseline learning for personalized gait analysis",
      "Used sensor fusion combining 6-axis motion and pressure data",
      "Developed real-time processing on Raspberry Pi 4 at 10Hz sampling rate",
      "Built prototype with off-the-shelf components for under $50",
      "Achieved 95% accuracy through iterative testing and algorithm refinement",
    ],
    category: "Hardware + ML",
    hackathon: "48-hour Hackathon",
    teamRole: "Technical Lead - Hardware/Software Integration",
    teamMembers: [
      "Arunav Saha (Technical Lead)",
      "Ejaz Khan (ML Developer)",
      "Musayed Ahmed (Hardware Engineer)",
    ],
    showcasePath: "/solemate",
  },
  {
    id: 5,
    title: "Dungeon of the Wild",
    description:
      "Action-adventure RPG with custom-built maps, strategic combat, and AI-driven enemies. Built from scratch using C++ and SFML.",
    longDescription:
      "Dungeon of the Wild is an action-adventure RPG developed as the final project for CS4300: Game Development. The game features custom-built overworld and dungeon maps where players battle various enemies and challenge powerful bosses. Built entirely from scratch using C++ and SFML, the game implements advanced features including vector field pathfinding for enemy AI, raycasting for line-of-sight detection, physics-driven movement with collision detection, and real-time shadow effects. Players can utilize multiple weapons (sword, arrows, beam), dash with invincibility frames, and experience various status effects. The project encompasses 7,564 lines of custom C++ code and demonstrates comprehensive game development principles from rendering and physics to AI and game design.",
    thumbnail: "/assets/images/project-dungeon.jpg",
    tags: [
      "C++",
      "SFML",
      "Game Development",
      "AI",
      "Physics Engine",
      "Raycasting",
    ],
    featured: true,
    buttons: [
      {
        text: "View on GitHub",
        color: "gray",
        link: "https://github.com/ARNAB814/Dungeon_of_The_Wild",
      },
      {
        text: "Watch Trailer",
        color: "red",
        link: "https://www.youtube.com/watch?v=OAiTauOFOWk",
      },
      {
        text: "Project Showcase",
        color: "orange",
        link: "/dungeon",
      },
    ],
    images: [
      "/assets/images/project-dungeon-1.jpg",
      "/assets/images/project-dungeon-2.jpg",
    ],
    challenges: [
      "Building a complete game engine from scratch using C++ and SFML",
      "Implementing vector field pathfinding for smooth enemy AI movement",
      "Creating raycasting system for line-of-sight detection and lighting effects",
      "Balancing combat mechanics across multiple difficulty levels",
      "Optimizing rendering performance for multiple entities and parallax backgrounds",
    ],
    solutions: [
      "Developed custom physics engine with gravity, acceleration, and collision detection",
      "Implemented vector field pathfinding for intelligent enemy navigation",
      "Created raycasting system for visibility mechanics and dynamic lighting",
      "Built comprehensive combat system with melee/ranged weapons and status effects",
      "Optimized sprite batching and memory management for smooth 60 FPS gameplay",
      "Designed dynamic camera system with minimap for tactical awareness",
    ],
    category: "Game Development",
    courseCode: "CS4300: Game Development",
    institution: "Memorial University of Newfoundland",
    completionDate: "April 2024",
    teamMembers: [
      "Saurav Singh Chandel",
      "Jenna Hillier",
      "Arunav Saha",
      "Adrian Alarcon",
    ],
    codeStats: "7,564 lines of C++ code",
    showcasePath: "/dungeon",
  },
  {
    id: 6,
    title: "Pokémon GO: Badge Hunt",
    description:
      "Gamified community engagement platform with 44 custom challenges, real-time leaderboards, and automated badge tracking for local Pokémon GO players.",
    longDescription:
      "The St. John's Badge Hunt is a comprehensive web application designed to enhance community engagement for the local Pokémon GO Discord server. The platform features 44 unique challenges spanning collection tasks, battle achievements, location-based goals, and social activities. Built with a real-time leaderboard system, players compete to unlock badges by completing creative challenges like catching specific CP Pokémon, participating in raid lobbies, or posting in Discord channels. The system includes automated badge tracking, searchable challenge database, recent activity feeds, and detailed player profiles. With 29 active players and over 250 badge completions, the platform successfully gamifies community participation and encourages both casual and competitive gameplay.",
    thumbnail: "/assets/images/project-pokemon-badges.jpg",
    tags: [
      "React",
      "Web Development",
      "Community Platform",
      "Real-time Data",
      "Gamification",
      "Discord Integration",
    ],
    featured: false,
    buttons: [
      {
        text: "Visit Website",
        color: "blue",
        link: "https://sjpoke.netlify.app",
      },
      {
        text: "Project Showcase",
        color: "yellow",
        link: "/pokemon-badges",
      },
    ],
    images: [
      "/assets/images/project-pokemon-badges-1.jpg",
      "/assets/images/project-pokemon-badges-2.jpg",
    ],
    challenges: [
      "Designing 44 unique, creative challenges that appeal to different player types",
      "Building real-time leaderboard system with automated ranking updates",
      "Creating searchable badge database with multiple categories and filters",
      "Implementing user-friendly submission system integrated with Discord",
      "Balancing challenge difficulty to maintain long-term engagement",
    ],
    solutions: [
      "Developed comprehensive challenge categories spanning collection, battle, location, and social achievements",
      "Built dynamic leaderboard tracking 250+ badge completions across 29 players",
      "Implemented search and filter functionality for easy badge discovery",
      "Created clean, responsive interface optimized for mobile Discord users",
      "Integrated Discord submission workflow for seamless badge verification",
      "Designed tiered progression system with special rewards at milestone achievements",
    ],
    category: "Web Development",
    platform: "St. John's Pokémon GO Discord Server",
    scale: "29 active players, 44 challenges, 250+ badge completions",
    role: "Creator & Developer",
    showcasePath: "/pokemon-badges",
  },
  {
    id: 7,
    title: "Kessler Syndrome: Humanity's Prison",
    description:
      "Interactive data visualization dashboard exploring space debris growth and the potential cascading collision scenario that could trap humanity on Earth.",
    longDescription:
      "The Kessler Syndrome Dashboard is an award-winning interactive visualization exploring the theoretical scenario where space debris density becomes so high that cascading collisions could render space activities impossible for generations. Built with Python, Streamlit, and Plotly, the dashboard visualizes 30,000+ tracked debris objects, projects growth scenarios from 2025-2100, and allows users to interactively adjust parameters like launch rates and cleanup efforts. The simulation uses a mathematical model based on NASA collision dynamics, showing how different policy decisions affect debris accumulation. The project earned recognition through selection for the Johnson Geo Centre exhibition and submission for the Pro Vice Chancellor's Prize for undergraduate research at Memorial University.",
    thumbnail: "/assets/images/project-kessler.jpg",
    tags: [
      "Python",
      "Streamlit",
      "Plotly",
      "Data Visualization",
      "Interactive Dashboard",
      "Space Science",
    ],
    featured: true,
    buttons: [
      {
        text: "Launch Dashboard",
        color: "blue",
        link: "https://814-data.streamlit.app",
      },
      {
        text: "View on GitHub",
        color: "gray",
        link: "https://github.com/ARNAB814/Data_Visualization_Project",
      },
      {
        text: "Project Showcase",
        color: "purple",
        link: "/kessler",
      },
    ],
    images: [
      "/assets/images/project-kessler-1.jpg",
      "/assets/images/project-kessler-2.jpg",
    ],
    challenges: [
      "Modeling complex orbital debris dynamics with cascading collision effects",
      "Processing and visualizing 30,000+ tracked space objects across 70+ years",
      "Creating intuitive parameter controls for non-technical audiences",
      "Balancing scientific accuracy with accessible presentation",
      "Designing responsive visualizations that work across all devices",
    ],
    solutions: [
      "Implemented mathematical model based on NASA Orbital Debris Program collision parameters",
      "Built interactive simulation allowing real-time scenario comparison (best/worst/balanced cases)",
      "Created dynamic visualizations using Plotly for debris growth, night sky impact, and country responsibility",
      "Developed parameter controls for launch rates (100-500/year) and cleanup efforts (0.5%-5%)",
      "Designed mobile-friendly interface with Streamlit for seamless cross-device experience",
      "Processed GCAT catalog data to show historical accumulation from 1957-2025",
    ],
    category: "Data Visualization",
    courseCode: "COMP 4304: Data Visualization",
    institution: "Memorial University of Newfoundland",
    completionDate: "April 2025",
    teamMembers: ["Arunav Saha", "Farhan Ishrak Probandho"],
    awards: [
      "Selected for Johnson Geo Centre 'Our Future & Space' Exhibition",
      "Submitted for Pro Vice Chancellor's Prize for Undergraduate Research",
    ],
    showcasePath: "/kessler",
  },
  // Add this object to your projectsData array in src/data/projects.js

{
  id: 8, // adjust ID as needed
  title: "Christie Projector Control System",
  description:
    "Professional command sequencer for Christie GS Series projectors with QLab integration — build, test, and export automated bash scripts for live theatre productions.",
  longDescription:
    "A desktop application built with Electron and Node.js for controlling Christie GS Series projectors via TCP/IP API commands. The system provides a row-based command builder where users sequence projector API calls and timed delays, then either fire commands directly to the projector for real-time testing or export a QLab-compatible bash script for use in live productions. The application covers the full Christie API surface: power, shutter, lens positioning (zoom, focus, shift), input selection, image adjustments, color control, test patterns, and light source management. Presets allow operators to save and recall configurations (e.g., 'Scene 2 — Wide Shot') as JSON files. A real-time log panel displays command responses and connection status, and a built-in connection tester verifies projector reachability on port 3002 before a show.",
  thumbnail:  "/assets/images/project-christie.jpg",
  tags: [
    "Electron",
    "Node.js",
    "TCP/IP",
    "QLab Integration",
    "Theatre Automation",
    "Bash Scripting",
    "Live Production",
  ],
  featured: false,
  buttons: [
    {
      text: "View on GitHub",
      color: "gray",
      link: "https://github.com/ARNAB814/christie-gs2k-sequencer",
    },
  ],
  images: [],
  challenges: [
    "Translating Christie's ASCII command set into a user-friendly GUI without exposing raw syntax to operators",
    "Supporting direct ('Fire Now') and deferred (script export) execution from the same sequence builder",
    "Ensuring reliable TCP socket communication with 5-second timeouts and meaningful error feedback",
    "Generating portable bash scripts that work inside QLab Script Cues without modification",
    "Handling macOS Gatekeeper restrictions and cross-platform packaging with electron-builder",
  ],
  solutions: [
    "Built a categorized command dropdown covering all Christie GS Series API commands with optional value fields",
    "Implemented dual-mode execution: live socket dispatch for rehearsal and bash script export for show use",
    "Added real-time log panel showing raw responses and connection state for instant diagnostics",
    "Designed preset system for saving/loading JSON configurations per scene or venue",
    "Documented Gatekeeper quarantine removal and Windows Developer Mode workarounds for distribution",
  ],
  category: "Tools & Automation",
  version: "1.4.0",
  compatibility: "Christie GS Series (DW, DH, D models) — firmware with TCP/IP API on port 3002",
}
];